import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import { hideModal, showModal } from '../../../store/actions/modalActions';
import { showSidePanel, hideSidePanel } from '../../../store/actions/sidePanelActions';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { getMultiHubSocketService } from '../../../utils/MultiHubSocketService';
import Button from '../../shared/TailwindComponents/Button';
import Badge from '../../shared/TailwindComponents/Badge';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import Table from '../../shared/TailwindComponents/Table';
import SkeletonClientItem from '../../Clients/SkeletonClientItem';
import {
  PiFileLight,
  PiScalesLight,
  PiBookOpenLight,
  PiCloudArrowDownLight,
  PiCloudArrowUpLight,
  PiMagnifyingGlassLight,
  PiGavelLight,
  PiTrashLight,
  PiSortAscendingLight,
  PiDatabaseLight,
  PiCaretDownLight,
  PiFunnelLight,
  PiSparkleLight,
  PiSpinnerGapLight,
} from 'react-icons/pi';
import { MatterData } from '../MatterDetails';
import { hasCasesSupport } from '../matterConstants';
import { fetchMatterSources, addFileToMatter, uploadLegalSource, addSourceToMatter, streamMatterSource, streamDocument, removeSourceFromMatter, downloadDocument, deleteDocument } from '../../../store/actions/Matters/MatterActions';
import { formatDocumentName } from '../../../utils/functions';
import { naturalCompare } from '../../../utils/naturalSort';
import Thumbnail from '../../Settings/AIStudio/Thumbnail';
import ExternalURLPreview from './ExternalURLPreview';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import { useGenerateMore } from '../../../hooks/useGenerateMore';
import FindMoreProgress from '../FindMoreProgress';
import SubTabBar from '../../shared/TailwindComponents/SubTabBar';
import SourceGeneratePopover from './SourceGeneratePopover';
import apis from '../../../utils/apis';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, sourcesItemsTourSteps } from '../../shared/FeatureTour';

interface SourcesTabContentProps {
  matterData: MatterData | null;
  sources: any | null;
  setSources: React.Dispatch<React.SetStateAction<any>>;
  loading: boolean;
  userAccessLevel: 'edit' | 'admin' | 'view';
  onUpdate: () => void;
  hasDocuments?: boolean;
}

const SourcesTabContent: React.FC<SourcesTabContentProps> = ({
  matterData,
  sources,
  setSources,
  loading,
  userAccessLevel,
  onUpdate,
  hasDocuments = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { checkSectionGate } = useLicenseGate();
  const canEdit = userAccessLevel === 'edit' || userAccessLevel === 'admin';
  const matterUuid = matterData?.uuid || matterData?.MatterID || '';
  const jurisdiction = matterData?.jurisdiction || matterData?.Jurisdiction || '';
  const showCases = hasCasesSupport(jurisdiction);
  
  const [activeSourceTab, setActiveSourceTab] = useState<string>('documents');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const sortBy: 'name' | 'date' = 'name';
  const sortDir : 'asc' | 'desc' = 'asc';
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingItems, setDeletingItems] = useState<string[]>([]);
  const [isTabLoading, setIsTabLoading] = useState(loading);
  const [docTypeFilter, setDocTypeFilter] = useState<string>('all');
  const [evaluatingSourceIds, setEvaluatingSourceIds] = useState<Set<string>>(new Set());
  const evalGroupsRef = useRef<string[]>([]);
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const { isGenerating: isFindingMoreLaws, handleGenerateMore: handleFindMoreLaws, progress: findMoreLawsProgress } = useGenerateMore({
    matterUuid,
    targetStep: 'laws',
    onComplete: onUpdate,
  });

  const { isGenerating: isFindingMoreCases, handleGenerateMore: handleFindMoreCases, progress: findMoreCasesProgress } = useGenerateMore({
    matterUuid,
    targetStep: 'cases',
    onComplete: onUpdate,
  });

  const { isGenerating: isFindingMorePrepWorks, handleGenerateMore: handleFindMorePrepWorks, progress: findMorePrepWorksProgress } = useGenerateMore({
    matterUuid,
    targetStep: 'preparatory_works',
    onComplete: onUpdate,
  });

  const { isGenerating: isFindingMoreRegulations, handleGenerateMore: handleFindMoreRegulations, progress: findMoreRegulationsProgress } = useGenerateMore({
    matterUuid,
    targetStep: 'regulations',
    onComplete: onUpdate,
  });

  const { isGenerating: isFindingMoreAgencyDecisions, handleGenerateMore: handleFindMoreAgencyDecisions, progress: findMoreAgencyDecisionsProgress } = useGenerateMore({
    matterUuid,
    targetStep: 'agency_decisions',
    onComplete: onUpdate,
  });

  const [availableIndexCategories, setAvailableIndexCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!jurisdiction) return;
    let cancelled = false;
    apis.get(`/matters/available-indexes?country=${encodeURIComponent(jurisdiction)}`)
      .then((res: any) => {
        if (cancelled) return;
        const indexes: { category: string }[] = res.data?.indexes ?? [];
        const categories = new Set(indexes.map((idx) => idx.category));
        setAvailableIndexCategories(categories);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [jurisdiction]);

  // Cleanup PubSub listeners on unmount
  useEffect(() => {
    return () => {
      const multiHubService = getMultiHubSocketService();
      evalGroupsRef.current.forEach(group => {
        multiHubService.emit('ai_processing', 'leave', { group });
      });
    };
  }, []);

  // ── Document type classifications ──
  // Grouped by purpose for the upload modal
  const documentTypeGroups = [
    {
      label: t('Legal Sources'),
      types: [
        { value: 'law', label: t('Law / Statute') },
        { value: 'legislation', label: t('Legislation / Regulation') },
        ...(showCases ? [
          { value: 'case', label: t('Case / Precedent') },
          { value: 'precedent', label: t('Legal Precedent') },
        ] : []),
      ]
    },
    {
      label: t('Case Documents'),
      types: [
        { value: 'contract', label: t('Contract') },
        { value: 'brief', label: t('Legal Brief') },
        { value: 'pleading', label: t('Pleading') },
        { value: 'evidence', label: t('Evidence') },
        { value: 'memo', label: t('Memo / Opinion') },
      ]
    },
    {
      label: t('Case Files'),
      types: [
        { value: 'correspondence', label: t('Correspondence') },
        { value: 'general', label: t('General Document') },
        { value: 'other', label: t('Other') },
      ]
    },
  ];

  // Flat list for the upload modal Dropdown (grouped with separators)
  const documentTypes = documentTypeGroups.flatMap(group => group.types);

  // Map for display labels
  const documentTypeLabels: Record<string, string> = {};
  documentTypeGroups.forEach(g => g.types.forEach(dt => { documentTypeLabels[dt.value] = dt.label; }));

  // Category classification for grouping
  const getDocTypeCategory = (docType: string): string => {
    const legalSources = ['law', 'legislation', 'case', 'precedent'];
    const caseDocuments = ['contract', 'brief', 'pleading', 'evidence', 'memo'];
    if (legalSources.includes(docType)) return 'legal_source';
    if (caseDocuments.includes(docType)) return 'case_document';
    return 'case_file';
  };

  const getDocTypeCategoryLabel = (category: string): string => {
    switch (category) {
      case 'legal_source': return t('Legal Source');
      case 'case_document': return t('Case Document');
      case 'case_file': return t('Case File');
      default: return t('Case File');
    }
  };

  useEffect(() => {
    setIsTabLoading(loading);
  }, [loading]);

  const refreshSourcesData = async () => {
    const matterUuid = matterData?.uuid || matterData?.MatterID;
    if (!matterUuid) return;
    setIsTabLoading(true);
    try {
      const freshSources = await dispatch(fetchMatterSources(matterUuid));
      setSources(freshSources);
      onUpdate();
    } catch (error) {
    } finally {
      setIsTabLoading(false);
    }
  };

  const badgeClass = (name: string) => {
    const e = name.split('.').pop()?.toLowerCase() || '';
    if (["pdf"].includes(e)) return "bg-red-50 text-red-700 ring-1 ring-red-100";
    if (["doc", "docx"].includes(e)) return "bg-blue-50 text-blue-700 ring-1 ring-blue-100";
    if (["txt"].includes(e)) return "bg-gray-50 text-gray-700 ring-1 ring-gray-200";
    return "bg-slate-50 text-slate-700 ring-1 ring-slate-200";
  };

  const getFileExt = (name: string) => {
    const ext = name.split('.').pop()?.toLowerCase() || 'file';
    return ext.charAt(0).toUpperCase() + ext.slice(1);
  };

  const capitalize = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const sourceTabs = useMemo(() => {
    const rawDocs = sources?.user_documents;
    const docsArray = Array.isArray(rawDocs) ? rawDocs : (rawDocs?.documents || []);
    // Legal source uploads are routed to laws.json/precedents.json — exclude from uploads count
    const legalSourceTypeSet = ['law', 'legislation', 'case', 'precedent'];
    const nonLegalDocs = docsArray.filter((d: any) => !legalSourceTypeSet.includes(d.document_type));
    const docsCount = nonLegalDocs.length || 0;

    // Laws/cases count from their dedicated source files
    const lawsRaw = sources?.laws?.documents || sources?.laws || sources?.retrieved_laws?.citations || [];
    const lawsArray = Array.isArray(lawsRaw) ? lawsRaw : [];
    const lawsCount = lawsArray.length;
    const casesRaw = sources?.precedents?.documents || sources?.precedents || sources?.cases || sources?.retrieved_cases?.citations || [];
    const casesArray = Array.isArray(casesRaw) ? casesRaw : [];
    const casesCount = casesArray.length;

    // Additional source types
    const prepWorksRaw = sources?.preparatory_works?.documents || sources?.preparatory_works || [];
    const prepWorksArray = Array.isArray(prepWorksRaw) ? prepWorksRaw : [];
    const prepWorksCount = prepWorksArray.length;
    const regulationsRaw = sources?.regulations?.documents || sources?.regulations || [];
    const regulationsArray = Array.isArray(regulationsRaw) ? regulationsRaw : [];
    const regulationsCount = regulationsArray.length;
    const agencyDecRaw = sources?.agency_decisions?.documents || sources?.agency_decisions || [];
    const agencyDecArray = Array.isArray(agencyDecRaw) ? agencyDecRaw : [];
    const agencyDecCount = agencyDecArray.length;

    const customDatasources = sources?.custom_datasources || [];
    const dsCount = customDatasources.reduce((sum: number, ds: any) => sum + (ds.count || ds.documents?.length || 0), 0);
    const allCount = docsCount + lawsCount + (showCases ? casesCount : 0) + prepWorksCount + regulationsCount + agencyDecCount + dsCount;

    // Count documents by type category (for non-legal docs only)
    const legalSourceDocs = docsArray.filter((d: any) => getDocTypeCategory(d.document_type || 'general') === 'legal_source');
    const caseDocDocs = docsArray.filter((d: any) => getDocTypeCategory(d.document_type || 'general') === 'case_document');
    const generalDocs = docsArray.filter((d: any) => getDocTypeCategory(d.document_type || 'general') === 'general');

    const baseTabs = [
      {
        id: 'documents',
        label: t('Case Files'),
        icon: <PiFileLight className="h-3 w-3" />,
        count: docsCount,
        group: 'matter' as const,
      },
      {
        id: 'laws',
        label: t('Laws'),
        icon: <PiScalesLight className="h-3 w-3" />,
        count: lawsCount,
        group: 'research' as const,
      },
      ...(showCases ? [{
        id: 'cases',
        label: t('Precedents'),
        icon: <PiGavelLight className="h-3 w-3" />,
        count: casesCount,
        group: 'research' as const,
      }] : []),
      ...((prepWorksCount > 0 || availableIndexCategories.has('preparatory_works')) ? [{
        id: 'preparatory_works',
        label: t('Preparatory Works'),
        icon: <PiBookOpenLight className="h-3 w-3" />,
        count: prepWorksCount,
        group: 'research' as const,
      }] : []),
      ...((regulationsCount > 0 || availableIndexCategories.has('regulations')) ? [{
        id: 'regulations',
        label: t('Regulations'),
        icon: <PiScalesLight className="h-3 w-3" />,
        count: regulationsCount,
        group: 'research' as const,
      }] : []),
      ...((agencyDecCount > 0 || availableIndexCategories.has('agency_decisions')) ? [{
        id: 'agency_decisions',
        label: t('Agency Decisions'),
        icon: <PiGavelLight className="h-3 w-3" />,
        count: agencyDecCount,
        group: 'research' as const,
      }] : []),
    ];

    // Add dynamic tabs for each connected datasource
    for (const ds of customDatasources) {
      baseTabs.push({
        id: `datasource_${ds.datasource_uuid}`,
        label: ds.datasource_name || t('Data Source'),
        icon: <PiDatabaseLight className="h-3 w-3" />,
        count: ds.count || ds.documents?.length || 0,
        group: 'research' as const,
      });
    }

    return baseTabs;
  }, [sources, t, showCases, availableIndexCategories]);

  // Available document type filters for the documents tab (excludes legal sources)
  const availableDocTypes = useMemo(() => {
    const rawDocs = sources?.user_documents;
    const docsArray = Array.isArray(rawDocs) ? rawDocs : (rawDocs?.documents || []);
    const legalTypes = ['law', 'legislation', 'case', 'precedent'];
    const typeCounts: Record<string, number> = {};
    docsArray.forEach((doc: any) => {
      const dt = doc.document_type || 'general';
      if (!legalTypes.includes(dt)) {
        typeCounts[dt] = (typeCounts[dt] || 0) + 1;
      }
    });
    return typeCounts;
  }, [sources]);

  const currentList = useMemo(() => {
    // Helper to normalize law/research docs with origin marking
    const mapLawDocs = (rawDocs: any, sourceType: 'law' | 'case' | 'preparatory_work' | 'regulation' | 'agency_decision' = 'law') => {
      const lawDocs = rawDocs || [];
      const lawsArray = Array.isArray(lawDocs) ? lawDocs : (lawDocs?.documents || []);
      return lawsArray.map((doc: any) => ({
        ...doc,
        name: doc.title || doc.name || doc.filename || `Law ${doc.id}`,
        url: doc.external_url || doc.cdn_url || '',
        contentType: doc.content_type || (doc.blob_path?.endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream'),
        lastModified: doc.created_at || doc.uploaded_at || new Date().toISOString(),
        size: doc.characters || doc.size || doc.content?.length || 0,
        content: doc.summary || doc.content_preview || doc.snippet || doc.document_description || doc.content,
        id: doc.id || doc.file_uuid,
        blobPath: doc.blob_path,
        relevanceScore: doc.relevance_score,
        summary: doc.summary,
        description: doc.document_description,
        keywords: doc.keywords,
        practicalApplication: doc.practical_application,
        country: doc.country,
        language: doc.language,
        searchScore: doc.search_score,
        rerankerScore: doc.search_reranker_score,
        justification: doc.justification,
        _origin: (doc.origin === 'user_upload' ? 'user_upload' : 'ai_analysis') as 'user_upload' | 'ai_analysis',
        _sourceType: sourceType,
        evaluationStatus: doc.evaluation_status,
        relevantSections: Array.isArray(doc.relevant_sections)
          ? doc.relevant_sections.map((s: any) =>
              typeof s === 'string' ? s : (s.section_name ? `${s.section_name}: ${s.section_content || ''}` : s.section_content || String(s))
            )
          : doc.relevant_sections,
      }));
    };

    // Helper to normalize case docs with origin marking
    const mapCaseDocs = (rawDocs: any) => {
      const caseDocs = rawDocs || [];
      const casesArray = Array.isArray(caseDocs) ? caseDocs : (caseDocs?.documents || []);
      return casesArray.map((doc: any) => ({
        ...doc,
        name: doc.title || doc.name || doc.filename || `Case ${doc.id}`,
        url: doc.external_url || doc.cdn_url || '',
        contentType: doc.content_type || (doc.blob_path?.endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream'),
        lastModified: doc.created_at || doc.uploaded_at || new Date().toISOString(),
        size: doc.characters || doc.size || doc.content?.length || 0,
        content: doc.summary || doc.content_preview || doc.snippet || doc.document_description || doc.content,
        id: doc.id || doc.file_uuid,
        blobPath: doc.blob_path,
        relevanceScore: doc.relevance_score,
        summary: doc.summary,
        description: doc.document_description,
        keywords: doc.keywords,
        practicalApplication: doc.practical_application,
        country: doc.country,
        language: doc.language,
        searchScore: doc.search_score,
        rerankerScore: doc.search_reranker_score,
        justification: doc.justification,
        _origin: (doc.origin === 'user_upload' ? 'user_upload' : 'ai_analysis') as 'user_upload' | 'ai_analysis',
        _sourceType: 'case' as const,
        evaluationStatus: doc.evaluation_status,
        relevantSections: Array.isArray(doc.relevant_sections)
          ? doc.relevant_sections.map((s: any) =>
              typeof s === 'string' ? s : (s.section_name ? `${s.section_name}: ${s.section_content || ''}` : s.section_content || String(s))
            )
          : doc.relevant_sections,
      }));
    };

    // Helper to normalize user documents with origin marking
    // Legal source types that are routed to laws.json/precedents.json by the backend
    const LEGAL_SOURCE_TYPES = ['law', 'legislation', 'case', 'precedent'];

    const mapUserDocs = (rawDocs: any) => {
      const docData = rawDocs || [];
      const docsArray = Array.isArray(docData) ? docData : (docData?.documents || []);
      // Filter out legal source uploads — they appear in Laws/Precedents tabs instead
      const filteredDocs = docsArray.filter((doc: any) =>
        !LEGAL_SOURCE_TYPES.includes(doc.document_type)
      );
      return filteredDocs.map((doc: any) => ({
        ...doc,
        name: doc.filename || doc.title || t('Untitled'),
        url: doc.blob_url || '',
        contentType: doc.content_type || 'application/octet-stream',
        lastModified: doc.uploaded_at || new Date().toISOString(),
        size: doc.size || 0,
        content: doc.summary || doc.content_preview || doc.snippet || doc.document_description || doc.content,
        id: doc.blob_path || doc.id,
        encrypted: doc.encrypted || false,
        encryptionFormat: doc.encryption_format,
        blobPath: doc.blob_path,
        type: 'document',
        _origin: 'user_upload' as const,
        _sourceType: 'document' as const,
        documentType: doc.document_type || 'general',
        relevanceScore: doc.relevance_score,
        summary: doc.summary,
        description: doc.document_description,
        keywords: doc.keywords,
        practicalApplication: doc.practical_application,
        country: doc.country,
        language: doc.language,
        justification: doc.justification,
        relevantSections: doc.relevant_sections,
      }));
    };

    // Helper to normalize datasource docs with origin marking
    const mapDatasourceDocs = (dsData: any) => {
      if (!dsData) return [];
      const dsDocs = Array.isArray(dsData.documents) ? dsData.documents : [];
      return dsDocs.map((doc: any) => ({
        ...doc,
        name: doc.title || doc.name || doc.file_name || `Document ${doc.id}`,
        url: doc.external_url || doc.cdn_url || '',
        contentType: doc.content_type || doc.file_type || (doc.blob_path?.endsWith('.pdf') || doc.file_path?.endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream'),
        lastModified: doc.uploaded_at || doc.last_modified || doc.created_at || new Date().toISOString(),
        size: doc.file_size || doc.characters || doc.content?.length || 0,
        content: doc.summary || doc.content_preview || doc.snippet || doc.document_description || doc.content,
        id: doc.id,
        blobPath: doc.blob_path || doc.file_path,
        relevanceScore: doc.relevance_score,
        summary: doc.summary,
        description: doc.document_description,
        keywords: doc.keywords,
        practicalApplication: doc.practical_application,
        country: doc.country,
        language: doc.language,
        searchScore: doc.search_score,
        rerankerScore: doc.search_reranker_score,
        justification: doc.justification,
        _origin: 'ai_analysis' as const,
        _sourceType: 'datasource' as const,
        _datasourceName: dsData.datasource_name || t('Data Source'),
        relevantSections: Array.isArray(doc.relevant_sections)
          ? doc.relevant_sections.map((s: any) =>
              typeof s === 'string' ? s : (s.section_name ? `${s.section_name}: ${s.section_content || ''}` : s.section_content || String(s))
            )
          : doc.relevant_sections,
      }));
    };

    switch (activeSourceTab) {
      case 'uploaded':
        return (sources?.user_sources || sources?.bulk_files || [])?.map((file: any) => ({
          name: file.title || file.label || t('Untitled'),
          url: file.blob_url || '',
          contentType: 'application/pdf',
          lastModified: file.created_at || file.lastModified || new Date().toISOString(),
          size: file.content?.length || 0,
          content: file.content,
          source_id: file.id || file.source_id,
          id: file.id,
          _origin: 'user_upload' as const,
          _sourceType: 'file' as const,
        })) || [];

      case 'laws':
        return mapLawDocs(sources?.laws || sources?.retrieved_laws?.citations);

      case 'cases':
        return mapCaseDocs(sources?.precedents || sources?.cases || sources?.retrieved_cases?.citations);

      case 'preparatory_works':
        return mapLawDocs(sources?.preparatory_works, 'preparatory_work');

      case 'regulations':
        return mapLawDocs(sources?.regulations, 'regulation');

      case 'agency_decisions':
        return mapLawDocs(sources?.agency_decisions, 'agency_decision');

      case 'documents':
        return mapUserDocs(sources?.user_documents);

      default: {
        // Handle dynamic datasource tabs (datasource_{uuid})
        if (activeSourceTab.startsWith('datasource_')) {
          const dsUuid = activeSourceTab.replace('datasource_', '');
          const customDatasources = sources?.custom_datasources || [];
          const dsData = customDatasources.find(
            (ds: any) => ds.datasource_uuid === dsUuid
          );
          return mapDatasourceDocs(dsData);
        }
        return [];
      }
    }
  }, [activeSourceTab, sources]);

  const sortedFilteredList = useMemo(() => {
    let filtered = currentList.filter((item: any) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Apply document type filter when on documents tab
    if (activeSourceTab === 'documents' && docTypeFilter !== 'all') {
      filtered = filtered.filter((item: any) => {
        const dt = item.documentType || item.document_type || 'general';
        if (docTypeFilter.startsWith('cat_')) {
          // Filter by category
          const cat = docTypeFilter.replace('cat_', '');
          return getDocTypeCategory(dt) === cat;
        }
        return dt === docTypeFilter;
      });
    }

    return filtered.sort((a: any, b: any) => {
      // For research tabs, prioritize by relevance score (highest first)
      const researchTabs = ['laws', 'cases', 'preparatory_works', 'regulations', 'agency_decisions'];
      if ((researchTabs.includes(activeSourceTab) || activeSourceTab.startsWith('datasource_')) && (a.relevanceScore || b.relevanceScore)) {
        const aScore = a.relevanceScore || 0;
        const bScore = b.relevanceScore || 0;
        return bScore - aScore; // Descending order (highest relevance first)
      }

      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortBy === 'name') {
        return naturalCompare(a.name, b.name) * dir;
      } else {
        const aDate = new Date(a.lastModified).getTime();
        const bDate = new Date(b.lastModified).getTime();
        return (aDate - bDate) * dir;
      }
    });
  }, [currentList, searchTerm, sortBy, sortDir, activeSourceTab, docTypeFilter]);

  const {
    isRunning: isItemsTourRunning,
    handleJoyrideCallback: handleItemsTourCallback,
  } = useFeatureTour({
    tourId: 'sources-items',
    steps: sourcesItemsTourSteps,
    readyToShow: currentList.length > 0 && !loading,
    autoStartDelay: 600,
  });

  // ── CASE FILES: Upload evidence, briefs, contracts, etc. ──
  const handleUploadCaseFiles = async () => {
    if (!checkSectionGate('files')) return;
    try {
      dispatch(
        showSidePanel({
          type: 'uploadAttachment',
          showSidePanel: true,
          width: '50%',
          message: t('Upload Case Files'),
          icon: <PiFileLight className="w-5 h-5" />,
          subMsg: t('Upload evidence, contracts, briefs, pleadings, memos, or other case-related documents.'),
          onConfirm: async (data: any) => {
            const uploadFiles: File[] = data.files || data;
            if (!uploadFiles.length) return;
            const matterUuid = matterData?.uuid || matterData?.MatterID;
            if (!matterUuid) return;

            setIsTabLoading(true);
            const formData = new FormData();
            uploadFiles.forEach((file: File) => formData.append('bulk_files', file));

            try {
              await dispatch(addFileToMatter(matterUuid, formData));
              await refreshSourcesData();
              setActiveSourceTab('documents');
            } catch (error) {
            } finally {
              setIsTabLoading(false);
            }
          },
          files: [],
          onCancel: () => dispatch(hideSidePanel()),
        } as any)
      );
    } catch (error) {
    }
  };

  // ── LAWS: Add a law / statute / regulation via file upload ──
  // Helper: upload a legal source file and track PubSub evaluation
  const processSourceUpload = async (matterUuid: string, sourceType: 'law' | 'case', sourceData: any, file?: File) => {
    if (!file) return null;
    const result = await dispatch(uploadLegalSource(matterUuid, file, sourceType, sourceData?.title));
    const evalCorrelationId = result?.evaluation_correlation_id;
    const evalFileUuids: string[] = result?.evaluated_file_uuids || [];
    if (evalCorrelationId && evalFileUuids.length > 0) {
      setEvaluatingSourceIds(prev => {
        const next = new Set(prev);
        evalFileUuids.forEach((id: string) => next.add(id));
        return next;
      });

      let evalComplete = false;
      const clearEvalState = () => {
        evalComplete = true;
        setEvaluatingSourceIds(prev => {
          const next = new Set(prev);
          evalFileUuids.forEach((id: string) => next.delete(id));
          return next;
        });
        refreshSourcesData();
      };

      // PubSub tracking
      const groupName = `source_eval_${evalCorrelationId}`;
      evalGroupsRef.current.push(groupName);
      const multiHubService = getMultiHubSocketService();
      await multiHubService.connectToHub('ai_processing');
      multiHubService.emit('ai_processing', 'join', { group: groupName });
      const handleEvalMsg = (message: any) => {
        const d = message?.data || message;
        const msgGroupId = d?.groupId || d?.group_id;
        if (msgGroupId && msgGroupId !== groupName) return;
        if (d?.status === 'completed' || d?.status === 'error') {
          multiHubService.emit('ai_processing', 'leave', { group: groupName });
          multiHubService.off('ai_processing', '*', handleEvalMsg);
          clearEvalState();
        }
      };
      multiHubService.on('ai_processing', '*', handleEvalMsg);

      // Fallback polling: if PubSub doesn't deliver completion within 45s,
      // poll the sources API to check if evaluation finished
      const pollInterval = setInterval(async () => {
        if (evalComplete) {
          clearInterval(pollInterval);
          return;
        }
        try {
          const freshSources = await dispatch(fetchMatterSources(matterUuid));
          const sourceFile = sourceType === 'law' ? 'laws' : 'precedents';
          const docs = freshSources?.[sourceFile]?.documents || freshSources?.[sourceFile] || [];
          const docsArray = Array.isArray(docs) ? docs : [];
          const allDone = evalFileUuids.every((fid: string) => {
            const doc = docsArray.find((d: any) => d.id === fid || d.file_uuid === fid);
            return doc && doc.evaluation_status === 'completed';
          });
          if (allDone) {
            multiHubService.emit('ai_processing', 'leave', { group: groupName });
            multiHubService.off('ai_processing', '*', handleEvalMsg);
            clearEvalState();
            clearInterval(pollInterval);
          }
        } catch {
          // Silent — keep polling
        }
      }, 15000); // Poll every 15 seconds

      // Safety: stop polling after 3 minutes regardless
      setTimeout(() => {
        clearInterval(pollInterval);
        if (!evalComplete) {
          clearEvalState();
        }
      }, 180000);
    }
    return result;
  };

  const handleAddLaw = async () => {
    if (!checkSectionGate('files')) return;
    const matterUuid = matterData?.uuid || matterData?.MatterID;
    if (!matterUuid) return;
    try {
      dispatch(
        showSidePanel({
          type: 'addSource',
          showSidePanel: true,
          width: '50%',
          sourceType: 'law',
          message: t('Add Laws or Statutes'),
          icon: <PiScalesLight className="w-5 h-5" />,
          subMsg: t('Upload one or more law, statute, or regulation documents. They will be automatically evaluated by AI for relevance.'),
          onConfirm: async (data: any) => {
            setIsTabLoading(true);
            try {
              if (data.files && data.files.length > 1) {
                for (const file of data.files) {
                  const title = file.name.replace(/\.[^/.]+$/, '');
                  await processSourceUpload(matterUuid, 'law', { title }, file);
                }
                dispatch(showFeedbackModal({ showModal: true, message: t('{{count}} laws uploaded successfully', { count: data.files.length }), modalType: 'success', duration: 3000 }));
              } else {
                await processSourceUpload(matterUuid, 'law', data.sourceData, data.file);
                dispatch(showFeedbackModal({ showModal: true, message: t('Law uploaded successfully'), modalType: 'success', duration: 3000 }));
              }
              await refreshSourcesData();
              setActiveSourceTab('laws');
            } catch (error) {
            } finally {
              setIsTabLoading(false);
            }
          },
          onCancel: () => dispatch(hideSidePanel()),
        } as any)
      );
    } catch (error) {
    }
  };

  // ── PRECEDENTS: Add a case / precedent via title + URL + optional file ──
  const handleAddPrecedent = async () => {
    if (!checkSectionGate('files')) return;
    const matterUuid = matterData?.uuid || matterData?.MatterID;
    if (!matterUuid) return;
    try {
      dispatch(
        showSidePanel({
          type: 'addSource',
          showSidePanel: true,
          width: '50%',
          sourceType: 'case',
          message: t('Add Case Precedents'),
          icon: <PiGavelLight className="w-5 h-5" />,
          subMsg: t('Upload one or more court case or precedent documents. They will be automatically evaluated by AI for relevance.'),
          onConfirm: async (data: any) => {
            setIsTabLoading(true);
            try {
              if (data.files && data.files.length > 1) {
                for (const file of data.files) {
                  const title = file.name.replace(/\.[^/.]+$/, '');
                  await processSourceUpload(matterUuid, 'case', { title }, file);
                }
                dispatch(showFeedbackModal({ showModal: true, message: t('{{count}} precedents uploaded successfully', { count: data.files.length }), modalType: 'success', duration: 3000 }));
              } else {
                await processSourceUpload(matterUuid, 'case', data.sourceData, data.file);
                dispatch(showFeedbackModal({ showModal: true, message: t('Precedent uploaded successfully'), modalType: 'success', duration: 3000 }));
              }
              await refreshSourcesData();
              setActiveSourceTab('cases');
            } catch (error) {
            } finally {
              setIsTabLoading(false);
            }
          },
          onCancel: () => dispatch(hideSidePanel()),
        } as any)
      );
    } catch (error) {
    }
  };


const handleOpenItem = async (item: any, type: 'file' | 'law' | 'case' | 'document') => {
  const matterUuid = matterData?.uuid || matterData?.MatterID;
  if (!matterUuid) return;

  const getSourceTypeLabel = () => {
    switch (activeSourceTab) {
      case 'cases': return t('Precedent');
      case 'laws': return t('Law');
      case 'documents': return t('Document');
      default: return t('Document');
    }
  };

  // Get blob_path from the item
  const blobPath = item.blobPath || item.blob_path;

  // Derive actual content type from blob_path extension when missing or generic
  const deriveContentType = (path: string, originalType?: string): string => {
    if (originalType && originalType !== 'application/octet-stream') return originalType;
    const ext = path.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'html': case 'htm': return 'text/html';
      case 'pdf': return 'application/pdf';
      case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'json': return 'application/json';
      case 'xml': return 'application/xml';
      case 'md': case 'markdown': return 'text/markdown';
      case 'txt': return 'text/plain';
      default: return originalType || 'application/octet-stream';
    }
  };
  const resolvedContentType = blobPath ? deriveContentType(blobPath, item.contentType) : item.contentType;

  if (!blobPath) {
    console.error('[Sources] No blob_path found for item:', item);
    dispatch(showFeedbackModal({
      modalType: 'error',
      message: t('File path not available for this source'),
      showModal: true,
      duration: 3000,
    }));
    return;
  }

  // Show side panel with loading state
  dispatch(showSidePanel({
    type: 'reader',
    matterId: matterUuid as string,
    sourceType: type as any,
    citation: {
      ...item,
      title: item.name || item.title,
      name: item.name || item.title,
      snippet: item.content || item.snippet,
      summary: item.summary,
      description: item.description,
      keywords: item.keywords,
      practicalApplication: item.practicalApplication,
      country: item.country,
      language: item.language,
      relevanceScore: item.relevanceScore,
      justification: item.justification,
      relevantSections: item.relevantSections,
      encrypted: item.encrypted,
      encryptionFormat: item.encryptionFormat,
      contentType: resolvedContentType || (item.url?.endsWith('.pdf') ? 'application/pdf' : 'text/html')
    },
    showSidePanel: true,
    loading: true,
    message: item.name || item.title,
    icon: <PiGavelLight className="w-6 h-6 text-gray-600" />,
    width: '85%',
    subMsg: getSourceTypeLabel(),
    onDelete: refreshSourcesData
  }));

  // Stream the file from backend
  try {
    const streamResult = await dispatch(streamMatterSource(blobPath, 'inline', resolvedContentType)) as any;

    if (!streamResult.success || !streamResult.blob) {
      console.error('[Sources] Failed to stream source:', streamResult.error);
      // Update side panel to stop loading - show metadata + "No content" fallback
      dispatch(showSidePanel({
        type: 'reader',
        matterId: matterUuid as string,
        sourceType: type as any,
        userAccessLevel,
        citation: {
          ...item,
          blob_path: undefined,
          blobPath: undefined,
          title: item.name || item.title,
          name: item.name || item.title,
          snippet: item.content || item.snippet,
          summary: item.summary,
          description: item.description,
          keywords: item.keywords,
          practicalApplication: item.practicalApplication,
          country: item.country,
          language: item.language,
          relevanceScore: item.relevanceScore,
          justification: item.justification,
          relevantSections: item.relevantSections,
          encrypted: item.encrypted,
          encryptionFormat: item.encryptionFormat,
          contentType: resolvedContentType,
        },
        showSidePanel: true,
        loading: false,
        message: item.name || item.title,
        icon: <PiGavelLight className="w-6 h-6 text-gray-600" />,
        width: '85%',
        subMsg: getSourceTypeLabel(),
        onDelete: refreshSourcesData
      }));
      return;
    }

    // Update the side panel with the blob directly (avoid blob URL issues)
    dispatch(showSidePanel({
      type: 'reader',
      matterId: matterUuid as string,
      sourceType: type as any,
      userAccessLevel,
      citation: {
        ...item,
        blob: streamResult.blob,
        blobObject: streamResult.blob,
        title: item.name || item.title,
        name: item.name || item.title,
        snippet: item.content || item.snippet,
        summary: item.summary,
        description: item.description,
        keywords: item.keywords,
        practicalApplication: item.practicalApplication,
        country: item.country,
        language: item.language,
        relevanceScore: item.relevanceScore,
        justification: item.justification,
        relevantSections: item.relevantSections,
        encrypted: false,
        encryptionFormat: undefined,
        contentType: streamResult.contentType || resolvedContentType || 'application/octet-stream'
      },
      showSidePanel: true,
      loading: false,
      message: item.name || item.title,
      icon: <PiGavelLight className="w-6 h-6 text-gray-600" />,
      width: '85%',
      subMsg: getSourceTypeLabel(),
      onDelete: refreshSourcesData
    }));
  } catch (error) {
    console.error('[Sources] Error streaming source:', error);
    // Update side panel to stop loading so user sees metadata + fallback
    dispatch(showSidePanel({
      type: 'reader',
      matterId: matterUuid as string,
      sourceType: type as any,
      userAccessLevel,
      citation: {
        ...item,
        title: item.name || item.title,
        name: item.name || item.title,
        snippet: item.content || item.snippet,
        summary: item.summary,
        description: item.description,
        keywords: item.keywords,
        practicalApplication: item.practicalApplication,
        country: item.country,
        language: item.language,
        relevanceScore: item.relevanceScore,
        justification: item.justification,
        relevantSections: item.relevantSections,
        encrypted: item.encrypted,
        encryptionFormat: item.encryptionFormat,
        contentType: resolvedContentType,
      },
      showSidePanel: true,
      loading: false,
      message: item.name || item.title,
      icon: <PiGavelLight className="w-6 h-6 text-gray-600" />,
      width: '85%',
      subMsg: getSourceTypeLabel(),
      onDelete: refreshSourcesData
    }));
  }
};

const handleDownloadDocument = async (item: any) => {
  const matterUuid = matterData?.uuid || matterData?.MatterID;
  if (!matterUuid) return;

  const blobPath = item.blobPath || item.blob_path;

  if (!blobPath) {
    console.error('[Sources] No blob_path found for document:', item);
    dispatch(showFeedbackModal({
      modalType: 'error',
      message: 'File path not available for this document',
      showModal: true,
      duration: 3000,
    }));
    return;
  }

  try {
    await dispatch(downloadDocument(matterUuid, blobPath, item.name || item.filename || 'document') as any);
  } catch (error) {
    console.error('[Sources] Error downloading document:', error);
    dispatch(showFeedbackModal({
      modalType: 'error',
      message: 'Error downloading document. Please try again.',
      showModal: true,
      duration: 3000,
    }));
  }
};

  const handleCopyLink = (item: any) => {
    const url = item.url;
    if (url) {
      navigator.clipboard.writeText(url);
    }
  };

  const handleDeleteItem = async (id: string, type: 'file' | 'law' | 'case' | 'document') => {
    const matterUuid = matterData?.uuid || matterData?.MatterID;
    if (!matterUuid) return;

    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      message: t('Delete Source'),
      subMsg: t('This source will be permanently removed from this matter. This action cannot be undone.'),
      onConfirm: async () => {
        setDeletingItems(prev => [...prev, id]);
        try {
          if (type === 'document') {
            // For documents, use the specialized delete endpoint
            const blobPath = id; // id is actually the blob_path for documents
            await dispatch(deleteDocument(matterUuid, blobPath) as any);
          } else {
            // For laws, cases, and files, use the generic remove endpoint
            await dispatch(removeSourceFromMatter(matterUuid, type, id) as any);
          }
          await refreshSourcesData();
        } catch (error) {
          console.error('Error deleting source:', error);
        } finally {
          setDeletingItems(prev => prev.filter(d => d !== id));
        }
        dispatch(hideModal());
      },
      onCancel: () => dispatch(hideModal()),
      icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
      isDest: true,
    }));
  };


  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleBulkRemove = () => {
    if (selectedSourceIds.length === 0) return;
    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      message: t('Remove {{count}} Sources', { count: selectedSourceIds.length }),
      subMsg: t('The selected sources will be permanently removed from this matter. This action cannot be undone.'),
      onConfirm: async () => {
        dispatch(hideModal());
        setIsBulkDeleting(true);
        setDeletingItems(prev => [...prev, ...selectedSourceIds]);
        try {
          for (const itemId of selectedSourceIds) {
            const item = currentList.find((i: any) => (i.source_id || i.id) === itemId);
            if (!item) continue;
            const itemType = item._sourceType || 'file';
            if (itemType === 'document') {
              await dispatch(deleteDocument(matterUuid, itemId) as any);
            } else {
              await dispatch(removeSourceFromMatter(matterUuid, itemType as any, itemId) as any);
            }
          }
          await refreshSourcesData();
        } catch (error) {
          console.error('Error bulk removing sources:', error);
        } finally {
          setDeletingItems(prev => prev.filter(d => !selectedSourceIds.includes(d)));
          setSelectedSourceIds([]);
          setIsBulkDeleting(false);
        }
      },
      onCancel: () => dispatch(hideModal()),
      icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
      isDest: true,
    }));
  };

  const handleSelectSource = (id: string) => {
    setSelectedSourceIds(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const handleSelectAllSources = () => {
    if (selectedSourceIds.length === sortedFilteredList.length) {
      setSelectedSourceIds([]);
    } else {
      setSelectedSourceIds(sortedFilteredList.map((item: any) => item.source_id || item.id));
    }
  };

const renderItemCard = (item: any, type: 'file' | 'law' | 'case' | 'document', mode: 'grid' | 'list' = 'list') => {
  const deleting = deletingItems.includes(item.source_id || item.id);
  const rawName = item.name || item.filename || `Item ${Math.random()}`;
  const { name, extension } = formatDocumentName(rawName);
  const preview = item.content?.substring(0, 100) || '';
  const url = item.url;
  
  const getTypeColors = () => {
    switch (type) {
      case 'file':
        return {
          iconColor: 'text-blue-600',
          bgGradient: 'from-blue-50 to-blue-100',
          badgeClass: badgeClass(name)
        };
      case 'document':
        return {
          iconColor: 'text-purple-600',
          bgGradient: 'from-purple-50/50 to-purple-100/50',
          badgeClass: badgeClass(name)
        };
      case 'law':
        return {
          iconColor: 'text-blue-500',
          bgGradient: 'from-blue-50/50 to-blue-100/50',
          badgeClass: 'bg-blue-50 text-blue-700 ring-1 ring-blue-100'
        };
      case 'case':
        return {
          iconColor: 'text-gray-600',
          bgGradient: 'from-gray-50/50 to-gray-100/50',
          badgeClass: 'bg-gray-50 text-gray-700 ring-1 ring-gray-100'
        };
      default:
        return {
          iconColor: 'text-gray-600',
          bgGradient: 'from-gray-50/50 to-gray-100/50',
          badgeClass: 'bg-gray-50 text-gray-700 ring-1 ring-gray-100'
        };
    }
  };

  const typeColors = getTypeColors();
  const icon = type === 'file' ? <PiFileLight className={`h-5 w-5 ${typeColors.iconColor}`} /> :
                 type === 'document' ? <PiCloudArrowDownLight className={`h-5 w-5 ${typeColors.iconColor}`} /> :
                 type === 'law' ? <PiScalesLight className={`h-5 w-5 ${typeColors.iconColor}`} /> :
                 <PiBookOpenLight className={`h-5 w-5 ${typeColors.iconColor}`} />;

  const cardClass = "group h-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 shadow-sm hover:shadow-md hover:border-blue-300 dark:hover:border-blue-700 transition-all duration-300 ease-in-out overflow-hidden";

  if (mode === 'grid') {
    return (
      <div className={cardClass}>
        <div className="relative overflow-hidden rounded-t-xl bg-gray-50/50 dark:bg-gray-800/50 h-48">
          {(type === 'law' || type === 'case') && item.url ? (
            <ExternalURLPreview
              url={item.url}
              title={item.name}
              contentType={item.contentType}
              relevanceScore={item.relevanceScore}
              justification={item.justification}
              summary={item.summary}
              type={type}
            />
          ) : (
            <>
              <Thumbnail
                url={item.url}
                name={item.name}
                contentType={item.contentType || 'application/pdf'}
                className="w-full h-auto"
                fallbackIcon={icon}
                preferPdfPreview={true}
              />
              {item.relevanceScore && type !== 'file' && (
                <div className="absolute top-2 right-2 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 px-2 py-1 rounded text-xs font-medium">
                  {Math.round(item.relevanceScore * 100)}%
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-4 flex-1 flex flex-col min-h-0">
          <div
            className="text-sm text-gray-900 dark:text-gray-100 truncate cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            title={name}
            onClick={() => handleOpenItem(item, type)}
          >
            {name}
          </div>
          {/* Origin badge */}
          {item._origin && (
            <div className="mt-1 flex items-center gap-1">
              {item._origin === 'user_upload' ? (
                <span className="inline-flex items-center gap-1 text-[10px] text-blue-600 dark:text-blue-400">
                  <PiCloudArrowUpLight className="h-3 w-3" /> {t('Uploaded')}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] text-purple-600 dark:text-purple-400">
                  <PiMagnifyingGlassLight className="h-3 w-3" /> {t('AI Found')}
                </span>
              )}
            </div>
          )}
          {preview && (
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-3 leading-relaxed">
              {preview}...
            </div>
          )}
          {item.country && type !== 'file' && (
            <div className="mt-2 text-xs text-gray-400 dark:text-gray-500 font-medium">
              {item.country}
            </div>
          )}
        </div>

        <div className="px-4 pb-4 pt-2 mt-auto flex items-center justify-between border-t border-gray-100 dark:border-gray-700">
          <Badge
            className={`text-xs font-medium ${typeColors.badgeClass}`}
          >
            {type === 'file' || type === 'document' ? (extension || 'File').toUpperCase() : capitalize(type)}
          </Badge>

          <div className="flex items-center gap-1">
            {canEdit && (
              <Button
                variant="destructive"
                size="small"
                onClick={() => handleDeleteItem(item.source_id || item.id, type)}
                title={t('Delete')}
              >
                {deleting ? (
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-t-2 border-b-2 border-white" />
                ) : (
                  t('Delete')
                )}
              </Button>
            )}
            <Button
              variant={type === 'document' ? 'primary' : 'outline'}
              size="small"
              onClick={() => handleOpenItem(item, type)}
              title={type === 'document' ? t('Download') : t('View')}
            >
              {type === 'document' ? t('Download') : t('Open')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${cardClass} flex items-start gap-3 p-3`}>
      <div className="shrink-0 overflow-hidden rounded-lg bg-gray-50/50 dark:bg-gray-800/50 w-40 h-32">
        {(type === 'law' || type === 'case') && item.url ? (
          <ExternalURLPreview
            url={item.url}
            title={item.name}
            contentType={item.contentType}
            relevanceScore={item.relevanceScore}
            justification={item.justification}
            summary={item.summary}
            type={type}
          />
        ) : (
          <Thumbnail
            url={item.url}
            name={item.name}
            contentType={item.contentType || 'application/pdf'}
            className="w-40 h-32"
            fallbackIcon={icon}
            preferPdfPreview={true}
          />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div
          className="text-sm text-gray-900 dark:text-gray-100 truncate cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          onClick={() => handleOpenItem(item, type)}
          title={name}
        >
          {name}
        </div>
        {/* Origin badge in row mode */}
        {item._origin && (
          <div className="mt-0.5 flex items-center gap-1">
            {item._origin === 'user_upload' ? (
              <span className="inline-flex items-center gap-1 text-[10px] text-blue-600 dark:text-blue-400">
                <PiCloudArrowUpLight className="h-3 w-3" /> {t('Uploaded')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] text-purple-600 dark:text-purple-400">
                <PiMagnifyingGlassLight className="h-3 w-3" /> {t('AI Found')}
              </span>
            )}
          </div>
        )}
        {preview && (
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed">
            {preview}...
          </div>
        )}
        <div className="mt-2 text-xs text-gray-400 flex flex-wrap gap-2 items-center">
          {url && (
            <button
              onClick={(e) => { e.stopPropagation(); window.open(url, '_blank'); }}
              className="text-blue-600 hover:text-blue-700 hover:underline text-xs transition-colors"
              title={t('Download')}
            >
              {t('Download')}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col items-end gap-2 shrink-0">
        <div className='flex gap-2 items-center mb-2 '>
        {item.lastModified && (
            <Badge>{formatDate(item.lastModified)}</Badge>
          )}
        <Badge
          className={`${typeColors.badgeClass}`}
        >
          {type === 'file' || type === 'document' ? (extension || 'File').toUpperCase() : capitalize(type)}
        </Badge>
        </div>

        <div className="flex flex-row items-center gap-1">
          {canEdit && (
            <Button
              variant="destructive"
              size="small"
              onClick={() => handleDeleteItem(item.source_id || item.id, type)}
              title={t('Delete')}
            >
              {deleting ? (
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-t-2 border-b-2 border-white" />
              ) : (
                t('Delete')
              )}
            </Button>
          )}
          <Button
            variant={type === 'document' ? 'primary' : 'outline'}
            size="small"
            onClick={() => handleOpenItem(item, type)}
            title={type === 'document' ? t('Download') : t('View')}
          >
            {type === 'document' ? t('Download') : t('Open')}
          </Button>
        </div>
      </div>
    </div>
  );
};

  const getTableColumns = () => {
    const researchTabs = ['laws', 'cases', 'preparatory_works', 'regulations', 'agency_decisions'];
    if (researchTabs.includes(activeSourceTab) || activeSourceTab.startsWith('datasource_')) {
      return ['Name', 'Relevance', 'Sections', 'Actions'];
    }
    if (activeSourceTab === 'documents') {
      return ['Name', 'Type', 'Category', 'Size', 'Actions'];
    }
    return ['Name', 'Type', 'Size', 'Actions'];
  };

  const getOriginLabel = (item: any) => {
    if (item._origin === 'user_upload') return t('User Upload');
    if (item._origin === 'ai_analysis') return t('AI Analysis');
    return t('Unknown');
  };

  const getCategoryLabel = (item: any) => {
    const st = item._sourceType;
    if (st === 'document') return capitalize(item.documentType || 'general');
    if (st === 'law') return t('Law');
    if (st === 'case') return t('Precedent');
    if (st === 'datasource') return item._datasourceName || t('Data Source');
    if (st === 'file') return t('File');
    return t('Document');
  };

  const renderTableRow = (item: any, type: 'file' | 'law' | 'case' | 'document') => {
    const rowData: Record<string, any> = {
      id: item.source_id || item.id,
    };

    // Strip file extension from name for display
    const fullName = item.name || t('Untitled');
    const nameWithoutExt = fullName.replace(/\.(docx?|pdf|xlsx?|pptx?|txt)$/i, '');
    
    // Map common fields
    const itemId = item.source_id || item.id || item.file_uuid;
    const isEvaluating = evaluatingSourceIds.has(itemId) || item.evaluationStatus === 'pending';
    rowData['Name'] = isEvaluating ? `${nameWithoutExt} (${t('Evaluating...')})` : nameWithoutExt;
    rowData['Type'] = type === 'file' ? getFileExt(item.name).toUpperCase() :
                      type === 'document' ? (documentTypeLabels[item.documentType] || capitalize(item.documentType || 'general')) :
                      capitalize(type);
    rowData['Doc Type'] = item.documentType ? (documentTypeLabels[item.documentType] || capitalize(item.documentType)) : getFileExt(item.name).toUpperCase();
    rowData['Category'] = type === 'document' 
      ? getDocTypeCategoryLabel(getDocTypeCategory(item.documentType || 'general'))
      : getCategoryLabel(item);
    rowData['Size'] = item.size ? `${(item.size / 1024).toFixed(2)} KB` : '-';

    // Origin and category for the "all" tab
    rowData['Origin'] = getOriginLabel(item);
    rowData['Category'] = getCategoryLabel(item);

    // Add rich fields for laws and cases
    if (type === 'law' || type === 'case') {
      rowData['Relevance'] = item.relevanceScore ? `${Math.round(item.relevanceScore * 100)}%` : '-';
      rowData['Sections'] = item.relevantSections?.length > 0
        ? t('{{count}} sections', { count: item.relevantSections.length })
        : '-';
    }

    return rowData;
  };

  const renderCurrentTab = () => {
    const total = currentList.length;
    const visible = sortedFilteredList.length;

    // Determine title and type for the active tab
    let title: string;
    let type: 'file' | 'law' | 'case' | 'document';

    if (activeSourceTab === 'uploaded') {
      title = t('Uploaded Files');
      type = 'file';
    } else if (activeSourceTab === 'documents') {
      title = t('Documents');
      type = 'document';
    } else if (activeSourceTab === 'laws') {
      title = t('Retrieved Laws');
      type = 'law';
    } else if (activeSourceTab === 'cases') {
      title = t('Retrieved Cases');
      type = 'case';
    } else if (activeSourceTab === 'preparatory_works') {
      title = t('Preparatory Works');
      type = 'law';
    } else if (activeSourceTab === 'regulations') {
      title = t('Regulations');
      type = 'law';
    } else if (activeSourceTab === 'agency_decisions') {
      title = t('Agency Decisions');
      type = 'law';
    } else if (activeSourceTab.startsWith('datasource_')) {
      // Dynamic datasource tab — find the name from sourceTabs
      const dsTab = sourceTabs.find((tab) => tab.id === activeSourceTab);
      title = dsTab?.label || t('Data Source');
      type = 'case'; // Reuse case-style rendering (has relevance, sections, etc.)
    } else {
      title = t('Sources');
      type = 'document';
    }

    // Tab-specific empty state configuration
    const emptyConfig = (() => {
      switch (activeSourceTab) {
        case 'documents':
          return {
            icon: <PiFileLight className="h-6 w-6 text-blue-500" />,
            iconBg: 'bg-blue-50 dark:bg-blue-900/30',
            title: t('No Case Files Yet'),
            description: t('Upload evidence, contracts, briefs, pleadings, and other documents related to your case.'),
            buttonLabel: t('Upload Case Files'),
            buttonIcon: <PiCloudArrowUpLight className="h-4 w-4" />,
            onButtonClick: handleUploadCaseFiles,
            showButton: canEdit,
          };
        case 'laws':
          return {
            icon: <PiScalesLight className="h-6 w-6 text-sky-500" />,
            iconBg: 'bg-sky-50 dark:bg-sky-900/30',
            title: t('No Laws or Statutes Yet'),
            description: t('Add relevant laws, statutes, and regulations. These will be AI-evaluated for relevance to your matter.'),
            buttonLabel: t('Add Law or Statute'),
            buttonIcon: <PiScalesLight className="h-4 w-4" />,
            onButtonClick: handleAddLaw,
            showButton: canEdit,
          };
        case 'cases':
          return {
            icon: <PiGavelLight className="h-6 w-6 text-amber-500" />,
            iconBg: 'bg-amber-50 dark:bg-amber-900/30',
            title: t('No Case Precedents Yet'),
            description: t('Add court cases and legal precedents. These will be AI-evaluated for relevance to your matter.'),
            buttonLabel: t('Add Case Precedent'),
            buttonIcon: <PiGavelLight className="h-4 w-4" />,
            onButtonClick: handleAddPrecedent,
            showButton: canEdit,
          };
        case 'preparatory_works':
          return {
            icon: <PiBookOpenLight className="h-6 w-6 text-teal-500" />,
            iconBg: 'bg-teal-50 dark:bg-teal-900/30',
            title: t('No Preparatory Works Yet'),
            description: t('Preparatory works (propositioner, utredningar) will appear here after analysis.'),
            buttonLabel: '',
            buttonIcon: null,
            onButtonClick: () => {},
            showButton: false,
          };
        case 'regulations':
          return {
            icon: <PiScalesLight className="h-6 w-6 text-indigo-500" />,
            iconBg: 'bg-indigo-50 dark:bg-indigo-900/30',
            title: t('No Regulations Yet'),
            description: t('Regulations (föreskrifter) will appear here after analysis.'),
            buttonLabel: '',
            buttonIcon: null,
            onButtonClick: () => {},
            showButton: false,
          };
        case 'agency_decisions':
          return {
            icon: <PiGavelLight className="h-6 w-6 text-orange-500" />,
            iconBg: 'bg-orange-50 dark:bg-orange-900/30',
            title: t('No Agency Decisions Yet'),
            description: t('Agency decisions will appear here after analysis.'),
            buttonLabel: '',
            buttonIcon: null,
            onButtonClick: () => {},
            showButton: false,
          };
        default:
          if (activeSourceTab.startsWith('datasource_')) {
            return {
              icon: <PiDatabaseLight className="h-6 w-6 text-purple-500" />,
              iconBg: 'bg-purple-50 dark:bg-purple-900/30',
              title: t('No Documents from Data Source'),
              description: t('Documents from this connected data source will appear here after analysis.'),
              buttonLabel: '',
              buttonIcon: null,
              onButtonClick: () => {},
              showButton: false,
            };
          }
          return {
            icon: <PiFileLight className="h-6 w-6 text-gray-500" />,
            iconBg: 'bg-gray-100 dark:bg-gray-700',
            title: t('No items yet.'),
            description: '',
            buttonLabel: '',
            buttonIcon: null,
            onButtonClick: () => {},
            showButton: false,
          };
      }
    })();

    if (total === 0) {
      return (
        <div className="flex flex-col items-center justify-center text-center rounded-xl p-10">
          <div className={`h-12 w-12 rounded-full ${emptyConfig.iconBg} flex items-center justify-center mb-3`}>
            {emptyConfig.icon}
          </div>
          <h3 className="text-gray-700 dark:text-gray-200 font-medium mb-1">{emptyConfig.title}</h3>
          <p className="text-gray-500 dark:text-gray-400 text-sm max-w-md">{emptyConfig.description}</p>
          {(emptyConfig.showButton || ['laws', 'cases', 'preparatory_works', 'regulations', 'agency_decisions'].includes(activeSourceTab)) && (
            <div className="flex items-center gap-2 mt-4">
              {emptyConfig.showButton && (
                <Button
                  variant={['laws', 'cases', 'preparatory_works', 'regulations', 'agency_decisions'].includes(activeSourceTab) ? 'outline' : 'primary'}
                  onClick={emptyConfig.onButtonClick}
                  size='small'
                  icon={emptyConfig.buttonIcon}
                >
                  {emptyConfig.buttonLabel}
                </Button>
              )}
              {(['laws', 'cases', 'preparatory_works', 'regulations', 'agency_decisions'].includes(activeSourceTab)) && (
                <SourceGeneratePopover
                  jurisdiction={jurisdiction}
                  sourceTab={activeSourceTab as any}
                  isGenerating={
                    activeSourceTab === 'laws' ? isFindingMoreLaws :
                    activeSourceTab === 'cases' ? isFindingMoreCases :
                    activeSourceTab === 'preparatory_works' ? isFindingMorePrepWorks :
                    activeSourceTab === 'regulations' ? isFindingMoreRegulations :
                    isFindingMoreAgencyDecisions
                  }
                  onGenerate={(selectedIndexes) => {
                    if (activeSourceTab === 'laws') handleFindMoreLaws(selectedIndexes);
                    else if (activeSourceTab === 'cases') handleFindMoreCases(selectedIndexes);
                    else if (activeSourceTab === 'preparatory_works') handleFindMorePrepWorks(selectedIndexes);
                    else if (activeSourceTab === 'regulations') handleFindMoreRegulations(selectedIndexes);
                    else handleFindMoreAgencyDecisions(selectedIndexes);
                  }}
                  hasDocuments={hasDocuments}
                  currentListCount={0}
                  canEdit={canEdit}
                />
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {visible === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 italic">{t('No items match your search.')}</p>
        ) : viewMode === 'table' ? (
          <div className="overflow-x-auto">
            <Table
              columns={getTableColumns()}
              data={sortedFilteredList.map((item: any) => {
                return {
                  ...renderTableRow(item, type as any),
                  _originalItem: item,
                };
              })}
              storageKeyPrefix={`sources-${activeSourceTab}`}
              enableSelection={canEdit}
              selectedIds={selectedSourceIds}
              onSelect={handleSelectSource}
              onSelectAll={handleSelectAllSources}
            renderCell={(column: string, value: any, row: any) => {
              if (column === 'Name') {
                const item = row._originalItem;
                const nameItemId = item.source_id || item.id || item.file_uuid;
                const nameIsEvaluating = evaluatingSourceIds.has(nameItemId) || item.evaluationStatus === 'pending';
                return (
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {nameIsEvaluating && (
                        <div className="flex-shrink-0 animate-spin rounded-full h-3.5 w-3.5 border-2 border-blue-200 border-t-blue-500" />
                      )}
                      <span className={`text-sm truncate ${nameIsEvaluating ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'}`}>
                        {value?.replace(` (${t('Evaluating...')})`, '')}
                      </span>
                      {nameIsEvaluating && (
                        <Badge color="blue">{t('Evaluating')}</Badge>
                      )}
                    </div>
                    {nameIsEvaluating && !item.justification && (
                      <div className="text-xs text-blue-500 dark:text-blue-400 animate-fadePulse">
                        {t('AI is analysing this document for relevance...')}
                      </div>
                    )}
                    {item.justification && (
                      <div
                        className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 whitespace-normal"
                        title={item.justification}
                      >
                        {item.justification}
                      </div>
                    )}
                  </div>
                );
              }
              if (column === 'Type' || column === 'Doc Type') {
                const item = row._originalItem;
                const docType = item?.documentType || item?.document_type || '';
                const cat = getDocTypeCategory(docType);
                const catColor = cat === 'legal_source' ? 'green' : cat === 'case_document' ? 'blue' : 'gray';
                return (
                  <Badge color={catColor as any}>
                    {value}
                  </Badge>
                );
              }
              if (column === 'Origin') {
                const item = row._originalItem;
                const isUserUpload = item._origin === 'user_upload';
                return (
                  <div className="flex items-center gap-1.5">
                    {isUserUpload ? (
                      <PiCloudArrowUpLight className="h-3.5 w-3.5 text-blue-500" />
                    ) : (
                      <PiMagnifyingGlassLight className="h-3.5 w-3.5 text-purple-500" />
                    )}
                    <Badge color={isUserUpload ? 'blue' : 'purple'}>
                      {value}
                    </Badge>
                  </div>
                );
              }
              if (column === 'Category') {
                const item = row._originalItem;
                const st = item?._sourceType;
                if (st === 'document') {
                  const docType = item?.documentType || item?.document_type || 'general';
                  const cat = getDocTypeCategory(docType);
                  const catColorMap: Record<string, string> = {
                    legal_source: 'green',
                    case_document: 'blue',
                    general: 'gray',
                  };
                  return (
                    <Badge color={(catColorMap[cat] || 'gray') as any}>
                      {value}
                    </Badge>
                  );
                }
                const colorMap: Record<string, string> = {
                  document: 'gray',
                  law: 'green',
                  case: 'yellow',
                  datasource: 'blue',
                  file: 'gray',
                };
                return (
                  <Badge color={(colorMap[st] || 'gray') as any}>
                    {value}
                  </Badge>
                );
              }
              if (column === 'Relevance') {
                const item = row._originalItem;
                const relItemId = item?.source_id || item?.id || item?.file_uuid;
                const relIsEvaluating = evaluatingSourceIds.has(relItemId) || item?.evaluationStatus === 'pending';
                if (relIsEvaluating) {
                  return (
                    <div className="animate-pulse h-5 w-12 bg-blue-100 dark:bg-blue-900/30 rounded" />
                  );
                }
                if (value !== '-') {
                  const score = parseInt(value);
                  const color = score >= 80 ? 'green' : score >= 60 ? 'blue' : 'yellow';
                  return (
                    <Badge color={color}>
                      {value}
                    </Badge>
                  );
                }
              }
              // if (column === 'Jurisdiction' && value !== '-') {
              //   return (
              //     <Badge color="gray">
              //       {value}
              //     </Badge>
              //   );
              // }
              if (column === 'Sections') {
                const item = row._originalItem;
                const secItemId = item?.source_id || item?.id || item?.file_uuid;
                const secIsEvaluating = evaluatingSourceIds.has(secItemId) || item?.evaluationStatus === 'pending';
                if (secIsEvaluating) {
                  return (
                    <div className="animate-pulse h-5 w-16 bg-blue-100 dark:bg-blue-900/30 rounded" />
                  );
                }
                if (value !== '-') {
                  return (
                    <Badge color="blue">
                      {value}
                    </Badge>
                  );
                }
              }
              return undefined;
            }}
            onRowClick={(row: any) => {
              if (row._originalItem) {
                const itemType = row._originalItem._sourceType || type;
                handleOpenItem(row._originalItem, itemType as any);
              }
            }}
            actions={[
              ...(type === 'document' ? [
                {
                  label: t('Download'),
                  icon: <PiCloudArrowDownLight className="h-4 w-4" />,
                  onClick: (row: any) => {
                    if (row._originalItem) handleDownloadDocument(row._originalItem);
                  }
                }
              ] : [
                {
                  label: t('View'),
                  icon: <PiBookOpenLight className="h-4 w-4" />,
                  onClick: (row: any) => {
                    if (row._originalItem) handleOpenItem(row._originalItem, type);
                  }
                }
              ]),
              {
                label: t('Remove'),
                icon: <PiTrashLight className="h-4 w-4" />,
                onClick: (row: any) => {
                  if (row._originalItem) {
                    const itemType = row._originalItem._sourceType || type;
                    handleDeleteItem(row._originalItem.source_id || row._originalItem.id, itemType as any);
                  }
                },
                isDestructive: true
              }
            ]}
            resizableColumns={true}
            defaultColumnWidths={
              activeSourceTab === 'documents'
                ? { 'Name': 350, 'Type': 150, 'Category': 130, 'Size': 100, 'Actions': 48 }
                : (type === 'law' || type === 'case')
                  ? { 'Name': 500, 'Relevance': 100, 'Sections': 150, 'Actions': 48 }
                  : { 'Name': 400, 'Type': 100, 'Doc Type': 100, 'Size': 120, 'Actions': 48 }
            }
            maxColumnWidths={{
              'Name': 800,
            }}
              max="max-h-[calc(100vh-240px)]"
              min="max-h-[calc(100vh-240px)]"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-[calc(100vh-280px)] overflow-y-auto custom-scrollbar-main">
            {sortedFilteredList.map((item: any) => {
              return (
                <div key={item.source_id || item.id} className="h-full">
                  {renderItemCard(item, type as any, 'grid')}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (isTabLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="animate-fadePulse bg-gray-200 dark:bg-gray-700 h-8 w-48 rounded"></div>
          <div className="animate-fadePulse bg-gray-200 dark:bg-gray-700 h-8 w-24 rounded"></div>
        </div>
        <SkeletonClientItem viewMode="table" count={5} />
      </div>
    );
  }

  if (!matterData) {
    return (
      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
        {t('Loading sources...')}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div data-tour-id="sources-sub-tabs">
      <SubTabBar
        tabs={sourceTabs}
        activeTab={activeSourceTab}
        onTabChange={(id) => { setActiveSourceTab(id as any); setDocTypeFilter('all'); setSelectedSourceIds([]); }}
        actions={
          <>
            {activeSourceTab === 'documents' && (
              <Button
                variant="primary"
                size="small"
                onClick={handleUploadCaseFiles}
                disabled={!canEdit}
                tooltip={!canEdit ? t('You need edit access to upload case files. Contact an editor or admin.') : ''}
                icon={<PiCloudArrowUpLight className="h-3.5 w-3.5" />}
              >
                {t('Upload Case Files')}
              </Button>
            )}
            {activeSourceTab === 'laws' && (
              <>
                {!isFindingMoreLaws && (
                  <Button
                    variant="outline"
                    size="small"
                    onClick={handleAddLaw}
                    disabled={!canEdit}
                    tooltip={!canEdit ? t('You need edit access to add laws. Contact an editor or admin.') : ''}
                    icon={<PiScalesLight className="h-3.5 w-3.5" />}
                  >
                    {t('Add Law')}
                  </Button>
                )}
                <SourceGeneratePopover
                  jurisdiction={jurisdiction}
                  sourceTab="laws"
                  isGenerating={isFindingMoreLaws}
                  onGenerate={(selectedIndexes) => handleFindMoreLaws(selectedIndexes)}
                  hasDocuments={hasDocuments}
                  currentListCount={currentList.length}
                  canEdit={canEdit}
                />
              </>
            )}
            {activeSourceTab === 'cases' && (
              <>
                {!isFindingMoreCases && (
                  <Button
                    variant="outline"
                    size="small"
                    onClick={handleAddPrecedent}
                    disabled={!canEdit}
                    tooltip={!canEdit ? t('You need edit access to add precedents. Contact an editor or admin.') : ''}
                    icon={<PiGavelLight className="h-3.5 w-3.5" />}
                  >
                    {t('Add Precedent')}
                  </Button>
                )}
                <SourceGeneratePopover
                  jurisdiction={jurisdiction}
                  sourceTab="cases"
                  isGenerating={isFindingMoreCases}
                  onGenerate={(selectedIndexes) => handleFindMoreCases(selectedIndexes)}
                  hasDocuments={hasDocuments}
                  currentListCount={currentList.length}
                  canEdit={canEdit}
                />
              </>
            )}
            {activeSourceTab === 'preparatory_works' && (
              <SourceGeneratePopover
                jurisdiction={jurisdiction}
                sourceTab="preparatory_works"
                isGenerating={isFindingMorePrepWorks}
                onGenerate={(selectedIndexes) => handleFindMorePrepWorks(selectedIndexes)}
                hasDocuments={hasDocuments}
                currentListCount={currentList.length}
                canEdit={canEdit}
              />
            )}
            {activeSourceTab === 'regulations' && (
              <SourceGeneratePopover
                jurisdiction={jurisdiction}
                sourceTab="regulations"
                isGenerating={isFindingMoreRegulations}
                onGenerate={(selectedIndexes) => handleFindMoreRegulations(selectedIndexes)}
                hasDocuments={hasDocuments}
                currentListCount={currentList.length}
                canEdit={canEdit}
              />
            )}
            {activeSourceTab === 'agency_decisions' && (
              <SourceGeneratePopover
                jurisdiction={jurisdiction}
                sourceTab="agency_decisions"
                isGenerating={isFindingMoreAgencyDecisions}
                onGenerate={(selectedIndexes) => handleFindMoreAgencyDecisions(selectedIndexes)}
                hasDocuments={hasDocuments}
                currentListCount={currentList.length}
                canEdit={canEdit}
              />
            )}
          </>
        }
      />
      </div>
      {/* Tab context description — subtle, matching Details page style */}
      {!activeSourceTab.startsWith('datasource_') && (
        <div className="mb-1 px-2 pt-1 pb-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            {activeSourceTab === 'documents' && t('Evidence, contracts, briefs, pleadings, correspondence, and other documents directly related to this matter.')}
            {activeSourceTab === 'laws' && t('Relevant laws, statutes, and regulations. These are evaluated by AI for applicability to your matter.')}
            {activeSourceTab === 'cases' && t('Court cases and legal precedents. These are evaluated by AI for relevance to your matter.')}
            {activeSourceTab === 'preparatory_works' && t('Legislative preparatory works and travaux préparatoires relevant to the legal issues in your matter.')}
            {activeSourceTab === 'regulations' && t('Government regulations and administrative rules applicable to your matter.')}
            {activeSourceTab === 'agency_decisions' && t('Decisions from government agencies and regulatory bodies relevant to your matter.')}
          </p>
        </div>
      )}
      {(activeSourceTab === 'laws' && (isFindingMoreLaws || findMoreLawsProgress.status === 'complete' || findMoreLawsProgress.status === 'failed')) ? (
        <FindMoreProgress progress={findMoreLawsProgress} isGenerating={isFindingMoreLaws} />
      ) : (activeSourceTab === 'cases' && (isFindingMoreCases || findMoreCasesProgress.status === 'complete' || findMoreCasesProgress.status === 'failed')) ? (
        <FindMoreProgress progress={findMoreCasesProgress} isGenerating={isFindingMoreCases} />
      ) : (activeSourceTab === 'preparatory_works' && (isFindingMorePrepWorks || findMorePrepWorksProgress.status === 'complete' || findMorePrepWorksProgress.status === 'failed')) ? (
        <FindMoreProgress progress={findMorePrepWorksProgress} isGenerating={isFindingMorePrepWorks} />
      ) : (activeSourceTab === 'regulations' && (isFindingMoreRegulations || findMoreRegulationsProgress.status === 'complete' || findMoreRegulationsProgress.status === 'failed')) ? (
        <FindMoreProgress progress={findMoreRegulationsProgress} isGenerating={isFindingMoreRegulations} />
      ) : (activeSourceTab === 'agency_decisions' && (isFindingMoreAgencyDecisions || findMoreAgencyDecisionsProgress.status === 'complete' || findMoreAgencyDecisionsProgress.status === 'failed')) ? (
        <FindMoreProgress progress={findMoreAgencyDecisionsProgress} isGenerating={isFindingMoreAgencyDecisions} />
      ) : (
      <div>

      <FilterBarWithTabs
        onSearchChange={setSearchTerm}
        viewMode={viewMode}
        setViewMode={setViewMode}
        noParentPadding={true}
        noParentHeight={true}
        itemCount={sortedFilteredList.length}
        totalItemCount={currentList.length}
        itemCountLabel={currentList.length === 1 ? t('item') : t('items')}
        selectionCount={selectedSourceIds.length}
        actions={canEdit && selectedSourceIds.length > 0 ? [
          {
            label: t('Remove Selected'),
            onClick: handleBulkRemove,
            icon: <PiTrashLight className="h-4 w-4 text-red-500" />,
          },
        ] : []}
      />

      {/* Document type filter chips — shown only on documents tab */}
      {activeSourceTab === 'documents' && Object.keys(availableDocTypes).length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5 pb-2">
          <PiFunnelLight className="h-3.5 w-3.5 text-gray-400 mr-1" />
          <button
            onClick={() => setDocTypeFilter('all')}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              docTypeFilter === 'all'
                ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 ring-1 ring-primary-300 dark:ring-primary-700'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            {t('All')} ({currentList.length})
          </button>
          {/* Category-level filters */}
          {(['legal_source', 'case_document', 'general'] as const).map(cat => {
            const catTypes = documentTypeGroups.find(g => 
              cat === 'legal_source' ? g.label === t('Legal Sources') :
              cat === 'case_document' ? g.label === t('Case Documents') :
              g.label === t('General')
            )?.types.map(t => t.value) || [];
            const catCount = catTypes.reduce((sum, dt) => sum + (availableDocTypes[dt] || 0), 0);
            if (catCount === 0) return null;
            const catColor = cat === 'legal_source' ? 'green' : cat === 'case_document' ? 'blue' : 'gray';
            const isActive = docTypeFilter === `cat_${cat}`;
            return (
              <button
                key={cat}
                onClick={() => setDocTypeFilter(isActive ? 'all' : `cat_${cat}`)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  isActive
                    ? `bg-${catColor}-100 dark:bg-${catColor}-900 text-${catColor}-700 dark:text-${catColor}-300 ring-1 ring-${catColor}-300 dark:ring-${catColor}-700`
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {getDocTypeCategoryLabel(cat)} ({catCount})
              </button>
            );
          })}
          {/* Individual type filters */}
          {Object.entries(availableDocTypes).map(([dt, count]) => {
            const isActive = docTypeFilter === dt;
            const cat = getDocTypeCategory(dt);
            const catColor = cat === 'legal_source' ? 'green' : cat === 'case_document' ? 'blue' : 'gray';
            return (
              <button
                key={dt}
                onClick={() => setDocTypeFilter(isActive ? 'all' : dt)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  isActive
                    ? `bg-${catColor}-100 dark:bg-${catColor}-900 text-${catColor}-700 dark:text-${catColor}-300 ring-1 ring-${catColor}-300 dark:ring-${catColor}-700`
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {documentTypeLabels[dt] || capitalize(dt)} ({count})
              </button>
            );
          })}
        </div>
      )}

      <div data-tour-id="sources-table-area" className="p-0 min-h-[400px] relative">
        {isBulkDeleting && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-lg">
            <PiSpinnerGapLight className="w-10 h-10 text-red-500 animate-spin" />
            <p className="mt-3 text-sm font-medium text-gray-900 dark:text-white">
              {t('Removing {{count}} sources...', { count: selectedSourceIds.length || deletingItems.length })}
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('Please wait, this may take a moment.')}
            </p>
          </div>
        )}
        {renderCurrentTab()}
      </div>
      </div>
      )}
      <FeatureTour
        steps={sourcesItemsTourSteps}
        run={isItemsTourRunning}
        onCallback={handleItemsTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default SourcesTabContent;
